import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Empty, Spin, Tooltip } from 'antd';
import { Global } from '@emotion/react';
import {
    HomeOutlined,
    UploadOutlined,
} from '@ant-design/icons';
import LockUnlocked from 'assets/svg/LockUnlocked';
import DuplicateIcon from 'assets/svg/DuplicateIcon';
import BulkUpdateIcon from 'assets/svg/BulkUpdateIcon';
import TrashIcon from 'assets/svg/TrashIcon';
import DownloadIcon from 'assets/svg/DownloadIcon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePriceSimulatorV3Context } from 'contexts/PriceSimulatorV3Context';
import { colorConstants } from 'config/colorConstants';
import PriceSimulatorCreateEditModal from 'components/molecules/PPA/Modals/PriceSimulatorCreateEditModal';
import { PpaScenarioActionType } from 'types/ppa';
import { ppaSimulationUrls } from 'config/urls';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';
import { PricingSimulatorNotificationMessages } from 'utils/Enums';
import {
    buildDuplicateScenarioPayload,
    buildRenameScenarioPayload,
    buildResetScenarioPayload,
    formatMissingPpaScopeMessage,
    getPpaScopeKeyLabelsFromScenarioManagerMeta,
    mapProjectDetailsToMetadataFields,
    resolvePpaCreateProjectApiErrorMessage,
    validatePpaScenarioNameForRename,
} from 'utils/ppaHelpers';
import type { PpaMetadataField } from 'utils/ppaHelpers';
import { getAuthUserMail, sendNotification } from 'utils/helperMethods';
import BulkUploadModalBridge from 'components/molecules/PricingSimulatorV3/Modals/BulkUploadModalBridge';
import DownloadTemplateModalBridge from 'components/molecules/PricingSimulatorV3/Modals/DownloadTemplateModalBridge';
import {
    toMetadataSummaryFields,
    usePpaScenarioMetadataSummary,
} from 'hooks/ppa/usePpaScenarioMetadataSummary';
import { SIMULATION_TABLE_QUERY_KEY } from 'components/molecules/PricingSimulatorV3/ScenarioLensTable/simulationTable.helpers';
import ScenarioLensSection from 'components/molecules/PricingSimulatorV3/ScenarioLensSection/ScenarioLensSection';
import type { ScenarioLensSectionController } from 'components/molecules/PricingSimulatorV3/ScenarioLensSection/scenarioLens.types';
import SimulationToolbar from 'components/molecules/PricingSimulatorV3/SimulationToolbar';
import SaveOnLeavePrompt from 'components/molecules/PricingSimulatorV3/SaveOnLeavePrompt';
import type { InlineEditableNameHandle } from 'components/molecules/PricingSimulatorV3/InlineEditableName';
import {
    showScenarioDeletedSuccessNotification,
    showScenarioDuplicatedSuccessNotification,
    showScenarioRenamedSuccessNotification,
    showResetInputsSuccessNotification,
} from 'components/molecules/PricingSimulatorV3/config/scenarioActions';
import { useScenarioNavigationBlocker } from 'components/molecules/PricingSimulatorV3/hooks/useScenarioNavigationBlocker';
import useScenarioWorkspaceInputs from 'components/molecules/PricingSimulatorV3/hooks/useScenarioWorkspaceInputs';
import ResetInputsConfirmationModal from 'components/molecules/PricingSimulatorV3/ResetInputsConfirmationModal';
import type { SimulatorAppliedFilters } from 'components/molecules/PricingSimulatorV3/SimulatorFilterPanel/SimulatorFilterPanel';
import SimulatorAppliedFiltersBar from 'components/molecules/PricingSimulatorV3/SimulatorFilterPanel/SimulatorAppliedFiltersBar';
import {
    DEFAULT_SIMULATOR_TABLE_DIMENSIONS,
    EMPTY_SIMULATOR_SHOW_ONLY_STATE,
} from 'components/molecules/PricingSimulatorV3/SimulatorFilterPanel/simulatorFilter.helpers';
import {
    enrichAppliedFiltersWithCategoryApplyMetadata,
    hasCompleteCategoryApplyMetadata,
    mergeSimulatorAppliedFiltersOnPanelApply,
} from 'components/molecules/PricingSimulatorV3/SimulatorFilterPanel/simulatorFilterApplyMetadata.helpers';
import {
    areSimulatorAppliedFiltersEqual,
    hasSimulatorAppliedFilters,
    hasPersistFilterContent,
    hydrateSimulatorSelectAllPreviewOptions,
    parseSimulatorAppliedFiltersFromPersistFilter,
} from 'components/molecules/PricingSimulatorV3/SimulatorFilterPanel/simulatorFilterPersist.helpers';
import { resolveSimulatorTableFiltersViaApplyFilter } from 'components/molecules/PricingSimulatorV3/SimulatorFilterPanel/simulatorFilterApply.helpers';
import { useSimulatorToolbarSearch } from 'components/molecules/PricingSimulatorV3/SimulatorFilterPanel/useSimulatorToolbarSearch';
import type { SelectedViewFromList } from 'hooks/ppa/ppaViewPrefetch.helpers';
import type { PPAViewDataSource, PPAPersistFilter } from 'types/ppaViews';
import {
    PPA_SIM_BREADCRUMB,
    PPA_SIM_BREADCRUMB_HOME_LINK,
    PPA_SIM_BREADCRUMB_MANAGER_LINK,
    PPA_SIM_BREADCRUMB_PROJECT,
    PPA_SIM_BREADCRUMB_SCENARIO,
    PPA_SIM_CONTENT,
    PPA_SIM_CREATE_SCENARIO_CANCEL_BTN,
    PPA_SIM_CREATE_SCENARIO_CONFIRM_BTN,
    PPA_SIM_CREATE_SCENARIO_MODAL,
    PPA_SIM_CREATE_SCENARIO_NAME_INPUT,
    PPA_SIM_EMPTY,
    PPA_SIM_LOADING,
    PPA_SIM_MORE_ACTIONS_BULK_UPDATE,
    PPA_SIM_MORE_ACTIONS_DELETE,
    PPA_SIM_MORE_ACTIONS_DUPLICATE,
    PPA_SIM_NO_SCOPE,
    PPA_SIM_PAGE,
    PPA_SIM_SCENARIO_PANEL,
    PPA_SIM_SCENARIO_TAB_ADD_BTN,
    PPA_SIM_SCENARIO_TABS,
    PPA_SIM_SCOPE_BAR,
    PPA_SIM_SCOPE_TOOLTIP,
    PPA_SIM_WORKSPACE,
    PPA_SIM_WORKSPACE_LOADING,
    ppaSimScenarioTabTestId,
    ppaSimScopeFieldTestId,
} from 'constants/ppaTestIds';
import {
    ScenarioTabLabel,
    StyledMetadataTooltipCard,
    StyledNewScenarioTabButton,
    StyledScenarioMetadataSummary,
    StyledScenarioPanel,
    StyledScenarioTabs,
    StyledScenarioTabsRow,
    MORE_ACTIONS_SUBMENU_POPUP_CLASS,
    moreActionsGlobalStyles,
} from './PriceSimulatorV3ProjectScenarios.styles';

const DUPLICATE_SCENARIO_MENU_KEY = 'duplicate_scenario';
const BULK_UPDATE_MENU_KEY = 'bulk_update';
const DOWNLOAD_TEMPLATE_MENU_KEY = 'download_template';
const UPLOAD_FILE_MENU_KEY = 'upload_file';
const DELETE_SCENARIO_MENU_KEY = 'delete_scenario';

type MoreActionsConfirmType = 'duplicate' | 'delete';

const SCENARIO_WORKSPACE_CONTENT_ID = 'price-simulator-v3-scenario-workspace-content';

type ScenarioViewSelection = {
    scenarioId: number | null;
    viewId: number | null;
    dataSource: PPAViewDataSource;
};

const Breadcrumb = ({
    basePath,
    projectName,
    scenarioName,
}: {
    basePath: string;
    projectName: string;
    scenarioName: string;
}) => (
    <nav className="flex items-center" aria-label="Breadcrumb" data-testid={PPA_SIM_BREADCRUMB}>
        <Link
            to="/"
            className="flex items-center text-black/50"
            aria-label="Home"
            data-testid={PPA_SIM_BREADCRUMB_HOME_LINK}
        >
            <HomeOutlined className="text-[12px]" />
        </Link>
        <span className="px-2 text-[10px] leading-[22px]" style={{ color: colorConstants.breadcrumbSlash }} aria-hidden>
            /
        </span>
        <Link
            to={basePath}
            className="text-[12px] leading-[22px]"
            style={{ color: colorConstants.colorTextDescription }}
            data-testid={PPA_SIM_BREADCRUMB_MANAGER_LINK}
        >
            Portfolio Planning &amp; Simulation
        </Link>
        <span className="px-2 text-[10px] leading-[22px]" style={{ color: colorConstants.breadcrumbSlash }} aria-hidden>
            /
        </span>
        <span
            className="text-[12px] leading-[22px]"
            style={{ color: colorConstants.colorTextDescription }}
            data-testid={PPA_SIM_BREADCRUMB_PROJECT}
        >
            {projectName}
        </span>
        <span className="px-2 text-[10px] leading-[22px]" style={{ color: colorConstants.breadcrumbSlash }} aria-hidden>
            /
        </span>
        <span
            className="text-[12px] font-medium leading-[22px]"
            style={{ color: colorConstants.blackTitle }}
            data-testid={PPA_SIM_BREADCRUMB_SCENARIO}
        >
            Scenario: {scenarioName}
        </span>
    </nav>
);

const METADATA_TOOLTIP_OVERLAY_INNER_STYLE = {
    padding: '20px',
    borderRadius: '12px',
    background: '#FFF',
    boxShadow:
        '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
};

const ScopeMetadataSummary = ({
    projectName,
    fields,
}: {
    projectName: string;
    fields: PpaMetadataField[];
}) => {
    const summaryBar = (
        <StyledScenarioMetadataSummary data-testid={PPA_SIM_SCOPE_BAR}>
            {toMetadataSummaryFields(fields).map((field) => (
                <span key={field.label} data-testid={ppaSimScopeFieldTestId(field.label)}>
                    <span className="metadata-label">{field.label}:</span>
                    <span className="metadata-value">{field.value}</span>
                </span>
            ))}
        </StyledScenarioMetadataSummary>
    );

    if (fields.length === 0) {
        return summaryBar;
    }

    return (
        <Tooltip
            destroyTooltipOnHide
            title={
                <StyledMetadataTooltipCard data-testid={PPA_SIM_SCOPE_TOOLTIP}>
                    <span className="tooltip-title">{projectName}</span>
                    <div className="tooltip-divider" />
                    <div className="tooltip-fields">
                        {fields.map((field) => (
                            <div key={field.label} className="tooltip-field">
                                <span className="tooltip-field-label">{field.label}</span>
                                {field.label === 'Project Visibility' && field.value === 'Private' ? (
                                    <span className="tooltip-visibility-value">
                                        <LockUnlocked />
                                        {field.value}
                                    </span>
                                ) : (
                                    <span className="tooltip-field-value">{field.value}</span>
                                )}
                            </div>
                        ))}
                    </div>
                </StyledMetadataTooltipCard>
            }
            overlayInnerStyle={METADATA_TOOLTIP_OVERLAY_INNER_STYLE}
            overlayStyle={{ width: 400, maxWidth: 'none' }}
            overlayClassName="metadata-tooltip-no-arrow"
            placement="bottomRight"
            align={{ offset: [-14, 0] }}
            color="#FFF"
        >
            {summaryBar}
        </Tooltip>
    );
};

const EMPTY_APPLIED_FILTERS: SimulatorAppliedFilters = {
    categorySelections: {},
    showOnly: EMPTY_SIMULATOR_SHOW_ONLY_STATE,
};

const PriceSimulatorV3ProjectScenarios = () => {
    const queryClient = useQueryClient();
    const location = useLocation();
    const { projectId: projectIdParam } = useParams<{ projectId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        projects,
        scenariosByProject,
        scenarioCountByProject,
        loadingScenarioProjectIds,
        scenarioManagerMeta,
        simulationToolbarConfig,
        simulationFilterConfig,
        fetchScenariosByProject,
        refreshScenariosForProject,
    } = usePriceSimulatorV3Context();
    const [viewSelection, setViewSelection] = useState<ScenarioViewSelection>({
        scenarioId: null,
        viewId: null,
        dataSource: 'view',
    });
    const [isViewListReady, setIsViewListReady] = useState(false);
    const [isScenarioViewModified, setIsScenarioViewModified] = useState(false);
    const [isCustomViewLoaded, setIsCustomViewLoaded] = useState(false);
    const [loadedViewName, setLoadedViewName] = useState('');
    const [isLensContentLoading, setIsLensContentLoading] = useState(true);
    const [moreActionsConfirm, setMoreActionsConfirm] = useState<MoreActionsConfirmType | null>(null);
    const [isMoreActionsOpen, setIsMoreActionsOpen] = useState(false);
    const [isDownloadTemplateModalOpen, setIsDownloadTemplateModalOpen] = useState(false);
    const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
    const [isViewDetached, setIsViewDetached] = useState(false);
    const [leavePromptOpen, setLeavePromptOpen] = useState(false);
    const [isLeaveActionSubmitting, setIsLeaveActionSubmitting] = useState(false);
    const [resetInputsModalOpen, setResetInputsModalOpen] = useState(false);
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState<SimulatorAppliedFilters>(EMPTY_APPLIED_FILTERS);
    const [baselineAppliedFilters, setBaselineAppliedFilters] =
        useState<SimulatorAppliedFilters>(EMPTY_APPLIED_FILTERS);
    const appliedFiltersRef = useRef(appliedFilters);
    appliedFiltersRef.current = appliedFilters;
    const baselineAppliedFiltersRef = useRef(baselineAppliedFilters);
    baselineAppliedFiltersRef.current = baselineAppliedFilters;
    const filterRestoreGenerationRef = useRef(0);
    const [resolvedTableFilters, setResolvedTableFilters] = useState<Record<string, number[]> | null>(null);
    const [isResolvingTableFilters, setIsResolvingTableFilters] = useState(false);
    const [_activeLensId, setActiveLensId] = useState<number | null>(null);

    const defaultFilterShowOnly = useMemo(
        () => ({
            edited: simulationFilterConfig.showOnly.edited.defaultSelected,
            impacted: simulationFilterConfig.showOnly.impacted.defaultSelected,
            editedFilters: [] as SimulatorAppliedFilters['showOnly']['editedFilters'],
        }),
        [
            simulationFilterConfig.showOnly.edited.defaultSelected,
            simulationFilterConfig.showOnly.impacted.defaultSelected,
        ],
    );
    const lensControllerRef = useRef<ScenarioLensSectionController | null>(null);
    const scenarioRenameRef = useRef<InlineEditableNameHandle | null>(null);
    const pendingNavigationRef = useRef<(() => void) | null>(null);
    const [isScenarioRenaming, setIsScenarioRenaming] = useState(false);
    const [projectDetailsFields, setProjectDetailsFields] = useState<PpaMetadataField[] | null>(null);
    const scenarioMetadataSummary = usePpaScenarioMetadataSummary(projectDetailsFields);

    const dismissTransientScenarioUi = useCallback(() => {
        setMoreActionsConfirm(null);
        setIsMoreActionsOpen(false);
        setIsFilterPanelOpen(false);
    }, []);

    const { mutate: deleteScenarioMutation, loading: isDeleteScenarioSubmitting } = useCommonMutationHandler({
        url: ppaSimulationUrls.deleteScenarioUrl,
        method: 'post',
    });
    const { mutate: renameScenarioMutation } = useCommonMutationHandler({
        url: ppaSimulationUrls.createScenarioUrl,
        method: 'post',
    });
    const { mutate: duplicateScenarioMutation, loading: isDuplicateScenarioSubmitting } = useCommonMutationHandler({
        url: ppaSimulationUrls.createScenarioUrl,
        method: 'post',
    });
    const { mutate: resetSimulatorMutation, loading: isResetInputsSubmitting } = useCommonMutationHandler({
        url: ppaSimulationUrls.resetSimulatorUrl,
        method: 'post',
    });

    const { mutate: getProjectDetailsMutation } = useCommonMutationHandler({
        url: ppaSimulationUrls.getProjectDetailsUrl,
        method: 'post',
        onSuccess: (data) => {
            const responseData = data?.data as Record<string, unknown> | undefined;
            if (responseData) {
                setProjectDetailsFields(mapProjectDetailsToMetadataFields(responseData));
            }
        },
    });

    const projectId = Number(projectIdParam);
    const scenarios = Number.isFinite(projectId) ? scenariosByProject[projectId] ?? [] : [];
    const scenarioCount = Number.isFinite(projectId) ? scenarioCountByProject[projectId] ?? scenarios.length : 0;
    const childLimit = scenarioManagerMeta?.child_limit;
    const isScenarioLimitReached = typeof childLimit === 'number' && scenarioCount >= childLimit;
    const isLoading = Number.isFinite(projectId) ? Boolean(loadingScenarioProjectIds[projectId]) : false;
    const project = projects.find((projectItem) => projectItem.project_id === projectId);
    const projectName = project?.project_name ?? `Project ${projectIdParam ?? ''}`;
    const selectedScenarioId = searchParams.get('scenarioId');
    const isCreateScenarioModalOpen = searchParams.get('createScenario') === '1';
    const basePath = useMemo(() => {
        const projectSegment = '/project/';
        const segmentIndex = location.pathname.indexOf(projectSegment);
        if (segmentIndex === -1) {
            return '/';
        }
        return location.pathname.slice(0, segmentIndex);
    }, [location.pathname]);

    useEffect(() => {
        if (!Number.isFinite(projectId)) {
            return;
        }
        void fetchScenariosByProject(projectId);
    }, [fetchScenariosByProject, projectId]);

    useEffect(() => {
        if (!Number.isFinite(projectId)) {
            return;
        }
        getProjectDetailsMutation({ project_id: projectId });
    }, [getProjectDetailsMutation, projectId]);

    useEffect(() => {
        if (!scenarios.length) {
            return;
        }

        const hasSelectedScenario = selectedScenarioId
            ? scenarios.some((scenario) => String(scenario.scenario_id) === selectedScenarioId)
            : false;

        if (!selectedScenarioId || !hasSelectedScenario) {
            setSearchParams({ scenarioId: String(scenarios[0].scenario_id) }, { replace: true });
        }
    }, [scenarios, selectedScenarioId, setSearchParams]);

    const selectedScenario = useMemo(
        () =>
            scenarios.find((scenario) => String(scenario.scenario_id) === String(selectedScenarioId)) ??
            scenarios[0],
        [scenarios, selectedScenarioId],
    );

    const {
        searchInput,
        searchKeyword,
        handleSearchChange,
        handleSearchSubmit,
    } = useSimulatorToolbarSearch({
        scenarioId: selectedScenario?.scenario_id,
        searchInputId: simulationToolbarConfig.searchInputId,
    });

    const scenarioTabs = useMemo(
        () =>
            scenarios.map((scenario) => ({
                key: String(scenario.scenario_id),
                label: (
                    <ScenarioTabLabel
                        title={scenario.scenario_name}
                        data-testid={ppaSimScenarioTabTestId(scenario.scenario_id)}
                    >
                        {scenario.scenario_name}
                    </ScenarioTabLabel>
                ),
            })),
        [scenarios],
    );

    const updateSearchParams = useCallback(
        (nextValues: Record<string, string | undefined>, replace = false) => {
            setSearchParams(
                (prev) => {
                    const nextParams = new URLSearchParams(prev);
                    Object.entries(nextValues).forEach(([key, value]) => {
                        if (value === undefined || value === '') {
                            nextParams.delete(key);
                        } else {
                            nextParams.set(key, value);
                        }
                    });
                    return nextParams;
                },
                { replace },
            );
        },
        [setSearchParams],
    );

    const closeCreateScenarioModal = () => {
        updateSearchParams({ createScenario: undefined });
    };

    const openCreateScenarioModal = () => {
        updateSearchParams({ createScenario: '1' });
    };

    const selectedScenarioName = selectedScenario?.scenario_name ?? 'Scenario';
    const hasValidPpaScope = selectedScenario?.ppa_scope_id != null;
    const missingPpaScopeMessage = useMemo(
        () =>
            formatMissingPpaScopeMessage(
                getPpaScopeKeyLabelsFromScenarioManagerMeta(scenarioManagerMeta),
            ),
        [scenarioManagerMeta],
    );
    const isScenarioOwner = useMemo(() => {
        const owner = (selectedScenario?.created_by ?? '').trim().toLowerCase();
        const current = (getAuthUserMail() ?? '').trim().toLowerCase();
        return Boolean(owner && owner === current);
    }, [selectedScenario?.created_by]);
    const {
        hasUserInputs,
        markUserInputsDirty,
        clearUserInputs,
    } = useScenarioWorkspaceInputs({
        scenarioId: selectedScenario?.scenario_id ?? null,
        enabled: isScenarioOwner,
    });
    const isResetInputsDisabled = !isScenarioOwner || !hasUserInputs;
    const isSimulateDisabled = !isScenarioOwner || !hasUserInputs;
    const resetInputsTooltip =
        isScenarioOwner && !hasUserInputs ? 'No inputs to reset' : undefined;
    const isCreateActionDisabled = !Number.isFinite(projectId) || !selectedScenario;
    const isDuplicateDisabled = isScenarioLimitReached || isCreateActionDisabled;
    const isLastScenarioInProject = scenarios.length <= 1;
    const isDeleteScenarioDisabled =
        isLastScenarioInProject || !isScenarioOwner || isCreateActionDisabled;
    const deleteScenarioTooltipTitle =
        isLastScenarioInProject && isScenarioOwner
            ? 'A project must have at least one scenario.'
            : undefined;

    const handleRenameScenario = useCallback(
        (newName: string) => {
            if (!selectedScenario || !Number.isFinite(projectId)) {
                return Promise.reject(new Error('Scenario unavailable'));
            }

            const payload = buildRenameScenarioPayload(projectId, selectedScenario.scenario_id, newName);

            return new Promise<void>((resolve, reject) => {
                renameScenarioMutation(payload, {
                    onSuccess: async () => {
                        await refreshScenariosForProject(projectId);
                        showScenarioRenamedSuccessNotification(newName);
                        resolve();
                    },
                    onError: (error: any) => {
                        const errorMessage = resolvePpaCreateProjectApiErrorMessage(
                            error,
                            PricingSimulatorNotificationMessages.SCENARIO_ACTION_ERROR,
                        );
                        sendNotification('error', errorMessage);
                        reject(error);
                    },
                });
            });
        },
        [projectId, refreshScenariosForProject, renameScenarioMutation, selectedScenario],
    );


    const handleDuplicateScenario = useCallback(() => {
        if (!selectedScenario || !Number.isFinite(projectId) || isScenarioLimitReached) {
            return;
        }

        const payload = buildDuplicateScenarioPayload(projectId, selectedScenario.scenario_name);

        duplicateScenarioMutation(payload, {
            onSuccess: async () => {
                setMoreActionsConfirm(null);
                await refreshScenariosForProject(projectId);
                showScenarioDuplicatedSuccessNotification();
            },
            onError: (error: any) => {
                const errorMessage = resolvePpaCreateProjectApiErrorMessage(
                    error,
                    PricingSimulatorNotificationMessages.SCENARIO_ACTION_ERROR,
                );
                sendNotification('error', errorMessage);
            },
        });
    }, [
        duplicateScenarioMutation,
        isScenarioLimitReached,
        projectId,
        refreshScenariosForProject,
        selectedScenario,
    ]);

    const activeScenarioId = selectedScenario?.scenario_id ?? null;
    const resolvedViewId =
        viewSelection.scenarioId === activeScenarioId ? viewSelection.viewId : null;
    const resolvedDataSource =
        viewSelection.scenarioId === activeScenarioId ? viewSelection.dataSource : 'view';

    const isFilterModified = useMemo(
        () =>
            !areSimulatorAppliedFiltersEqual(
                appliedFilters,
                baselineAppliedFilters,
                simulationFilterConfig.categories,
            ),
        [appliedFilters, baselineAppliedFilters, simulationFilterConfig.categories],
    );

    const isFilterActive = useMemo(
        () => hasSimulatorAppliedFilters(appliedFilters, simulationFilterConfig.categories),
        [appliedFilters, simulationFilterConfig.categories],
    );

    const isWorkspaceModified = isScenarioViewModified || isFilterModified;

    const handleSelectedViewChange = useCallback(
        (selection: SelectedViewFromList) => {
            if (selection.viewId != null) {
                setIsViewDetached(false);
            }

            setViewSelection((current) => {
                if (
                    current.scenarioId === activeScenarioId &&
                    current.viewId === selection.viewId &&
                    current.dataSource === selection.dataSource
                ) {
                    return current;
                }

                return {
                    scenarioId: activeScenarioId,
                    viewId: selection.viewId,
                    dataSource: selection.dataSource,
                };
            });
        },
        [activeScenarioId],
    );

    const handleViewListLoadingChange = useCallback((isLoading: boolean) => {
        setIsViewListReady(!isLoading);
    }, []);

    const handleLoadedViewChange = useCallback(
        (
            loadedViewId: number | null,
            isCustomView: boolean,
            viewName?: string,
            persistFilter?: PPAPersistFilter,
        ) => {
            const generation = ++filterRestoreGenerationRef.current;

            setIsCustomViewLoaded(isCustomView);
            setLoadedViewName(viewName?.trim() ?? '');

            const clearFilters = () => {
                setAppliedFilters(EMPTY_APPLIED_FILTERS);
                setBaselineAppliedFilters(EMPTY_APPLIED_FILTERS);
            };

            // Drop stale filters from the previous view immediately.
            clearFilters();

            if (
                loadedViewId == null ||
                !hasPersistFilterContent(persistFilter, simulationFilterConfig.categories)
            ) {
                return;
            }

            void (async () => {
                const parsed = parseSimulatorAppliedFiltersFromPersistFilter(
                    persistFilter,
                    simulationFilterConfig.categories,
                );
                const hydrated = await hydrateSimulatorSelectAllPreviewOptions(
                    parsed,
                    simulationFilterConfig.categories,
                    selectedScenario?.scope,
                );
                if (generation !== filterRestoreGenerationRef.current) {
                    return;
                }
                setAppliedFilters(hydrated);
                setBaselineAppliedFilters(hydrated);
            })();
        },
        [selectedScenario?.scope, simulationFilterConfig.categories],
    );

    useEffect(() => {
        filterRestoreGenerationRef.current += 1;
        setIsScenarioViewModified(false);
        setIsCustomViewLoaded(false);
        setLoadedViewName('');
        setIsLensContentLoading(true);
        setIsViewListReady(false);
        setIsViewDetached(false);
        setIsFilterPanelOpen(false);
        setAppliedFilters(EMPTY_APPLIED_FILTERS);
        setBaselineAppliedFilters(EMPTY_APPLIED_FILTERS);
        setResolvedTableFilters(null);
        setActiveLensId(null);
        setViewSelection((current) =>
            current.scenarioId === activeScenarioId
                ? current
                : {
                      scenarioId: activeScenarioId,
                      viewId: null,
                      dataSource: 'view',
                  },
        );
    }, [activeScenarioId]);

    useEffect(() => {
        const scenarioId = selectedScenario?.scenario_id;
        if (!scenarioId) {
            setResolvedTableFilters(null);
            return;
        }

        let cancelled = false;

        void (async () => {
            setIsResolvingTableFilters(true);
            try {
                const enrichedFilters = hasCompleteCategoryApplyMetadata(
                    appliedFilters,
                    simulationFilterConfig.categories,
                )
                    ? appliedFilters
                    : await enrichAppliedFiltersWithCategoryApplyMetadata(
                          appliedFilters,
                          simulationFilterConfig.categories,
                          selectedScenario.scope,
                          appliedFilters.categoryAttributeCache,
                      );
                const resolved = await resolveSimulatorTableFiltersViaApplyFilter({
                    filterCategories: simulationFilterConfig.categories,
                    appliedFilters: enrichedFilters,
                    appliedScope: selectedScenario.scope,
                });
                if (!cancelled) {
                    setResolvedTableFilters(resolved);
                    if (
                        !hasCompleteCategoryApplyMetadata(
                            appliedFilters,
                            simulationFilterConfig.categories,
                        ) &&
                        hasCompleteCategoryApplyMetadata(
                            enrichedFilters,
                            simulationFilterConfig.categories,
                        )
                    ) {
                        setAppliedFilters(enrichedFilters);
                    }
                }
            } catch {
                if (!cancelled) {
                    sendNotification('error', 'Failed to apply filters. Please try again.');
                    setResolvedTableFilters(
                        Object.fromEntries(DEFAULT_SIMULATOR_TABLE_DIMENSIONS.map((dimension) => [dimension, []])),
                    );
                }
            } finally {
                if (!cancelled) {
                    setIsResolvingTableFilters(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        appliedFilters,
        selectedScenario?.scenario_id,
        selectedScenario?.scope,
        simulationFilterConfig.categories,
    ]);

    const openLeavePrompt = useCallback((proceed: () => void) => {
        pendingNavigationRef.current = proceed;
        setLeavePromptOpen(true);
    }, []);

    const closeLeavePrompt = useCallback(() => {
        pendingNavigationRef.current = null;
        setLeavePromptOpen(false);
        setIsLeaveActionSubmitting(false);
    }, []);

    const completePendingNavigation = useCallback(() => {
        const proceed = pendingNavigationRef.current;
        pendingNavigationRef.current = null;
        setLeavePromptOpen(false);
        setIsLeaveActionSubmitting(false);
        setIsScenarioViewModified(false);
        setBaselineAppliedFilters(appliedFiltersRef.current);
        proceed?.();
    }, []);

    const runLeaveAction = useCallback(
        async (action: () => Promise<void>) => {
            setIsLeaveActionSubmitting(true);
            try {
                await action();
                completePendingNavigation();
            } catch {
                setIsLeaveActionSubmitting(false);
            }
        },
        [completePendingNavigation],
    );

    const handleLeaveUpdateMainView = useCallback(async () => {
        if (!lensControllerRef.current) {
            throw new Error('Lens controller unavailable');
        }
        await lensControllerRef.current.saveScenarioAndUpdateView();
    }, []);

    const handleLeaveIgnore = useCallback(async () => {
        if (!lensControllerRef.current) {
            throw new Error('Lens controller unavailable');
        }
        await lensControllerRef.current.saveScenarioAndPersistFiltersToView();
    }, []);

    const handleLeaveDiscard = useCallback(async () => {
        if (!lensControllerRef.current) {
            throw new Error('Lens controller unavailable');
        }
        await lensControllerRef.current.resetToPersisted();
        setAppliedFilters(baselineAppliedFiltersRef.current);
    }, []);

    const handleViewLoaded = useCallback(() => {
        setIsScenarioViewModified(false);
        setIsViewDetached(false);
    }, []);

    const handlePersistScenarioSnapshot = useCallback(async () => {
        if (!lensControllerRef.current) {
            throw new Error('Lens controller unavailable');
        }

        await lensControllerRef.current.saveScenarioView();
    }, []);

    const discardScenarioRename = useCallback(() => {
        scenarioRenameRef.current?.cancelEditing();
    }, []);

    const shouldPromptOnLeave = isScenarioOwner && isWorkspaceModified;

    useScenarioNavigationBlocker(
        isScenarioRenaming || shouldPromptOnLeave,
        ({ proceed }) => {
            discardScenarioRename();

            if (shouldPromptOnLeave) {
                openLeavePrompt(proceed);
                return;
            }

            proceed();
        },
    );

    useEffect(() => {
        discardScenarioRename();
    }, [discardScenarioRename, selectedScenarioId]);

    useEffect(() => {
        dismissTransientScenarioUi();
    }, [dismissTransientScenarioUi, selectedScenarioId]);

    const handleScenarioTabChange = useCallback(
        (nextTabKey: string) => {
            discardScenarioRename();
            dismissTransientScenarioUi();

            const proceed = () => updateSearchParams({ scenarioId: nextTabKey });

            if (shouldPromptOnLeave) {
                openLeavePrompt(proceed);
                return;
            }

            proceed();
        },
        [
            discardScenarioRename,
            dismissTransientScenarioUi,
            openLeavePrompt,
            shouldPromptOnLeave,
            updateSearchParams,
        ],
    );

    const handleNewScenarioClick = useCallback(() => {
        if (isScenarioLimitReached) {
            return;
        }

        discardScenarioRename();

        const proceed = () => openCreateScenarioModal();

        if (shouldPromptOnLeave) {
            openLeavePrompt(proceed);
            return;
        }

        proceed();
    }, [
        discardScenarioRename,
        isScenarioLimitReached,
        openCreateScenarioModal,
        openLeavePrompt,
        shouldPromptOnLeave,
    ]);

    const handleScenarioExitComplete = useCallback(() => {
        setIsScenarioViewModified(false);
    }, []);

    const handleExitViewRequested = useCallback((completeExit: () => void) => {
        completeExit();
        setIsViewDetached(true);
    }, []);

    const handleUpdateLoadedView = useCallback(async () => {
        if (!isScenarioOwner || !lensControllerRef.current) {
            return;
        }

        await lensControllerRef.current.saveScenarioAndUpdateView();
        setIsScenarioViewModified(false);
        setBaselineAppliedFilters(appliedFiltersRef.current);
    }, [isScenarioOwner]);

    const showScenarioWorkspaceLoader = !isViewListReady || isLensContentLoading;
    const scenarioWorkspaceContentHeight = useRef<number | null>(0);
    scenarioWorkspaceContentHeight.current = useFillHeightToFitScreen({
        id: SCENARIO_WORKSPACE_CONTENT_ID,
        height: showScenarioWorkspaceLoader ? 0 : scenarioWorkspaceContentHeight.current,
        buffer: 0,
        triggerDummyRender: true,
    });
    const resolvedScenarioWorkspaceContentHeight =
        typeof scenarioWorkspaceContentHeight.current === 'number' &&
        Number.isFinite(scenarioWorkspaceContentHeight.current)
            ? Math.max(320, scenarioWorkspaceContentHeight.current)
            : 320;

    const handleLensContentLoadingChange = useCallback((isLoading: boolean) => {
        setIsLensContentLoading(isLoading);
    }, []);

    const handleAppliedFiltersChange = useCallback(
        async (filters: SimulatorAppliedFilters): Promise<boolean> => {
            if (!selectedScenario?.scenario_id) {
                return false;
            }

            setAppliedFilters(
                mergeSimulatorAppliedFiltersOnPanelApply(appliedFiltersRef.current, filters),
            );
            return true;
        },
        [selectedScenario?.scenario_id],
    );

    const openMoreActionsConfirm = useCallback((confirmType: MoreActionsConfirmType) => {
        setMoreActionsConfirm(confirmType);
    }, []);

    const closeMoreActionsConfirm = useCallback(() => {
        setMoreActionsConfirm(null);
    }, []);

    const confirmDuplicateScenario = useCallback(() => {
        setIsMoreActionsOpen(false);
        window.setTimeout(() => {
            openMoreActionsConfirm('duplicate');
        }, 0);
    }, [openMoreActionsConfirm]);

    const confirmDeleteScenario = useCallback(() => {
        if (!selectedScenario || !Number.isFinite(projectId) || scenarios.length <= 1) {
            return;
        }
        setIsMoreActionsOpen(false);
        window.setTimeout(() => {
            openMoreActionsConfirm('delete');
        }, 0);
    }, [openMoreActionsConfirm, projectId, scenarios.length, selectedScenario]);

    const handleDeleteScenario = useCallback(() => {
        if (!selectedScenario || !Number.isFinite(projectId) || scenarios.length <= 1) {
            return;
        }

        const payload: Record<string, unknown> = {
            scenario_id: selectedScenario.scenario_id,
        };
        const createdBy = getAuthUserMail();
        if (createdBy) {
            payload.created_by = createdBy;
        }

        deleteScenarioMutation(payload, {
            onSuccess: async () => {
                setMoreActionsConfirm(null);
                await refreshScenariosForProject(projectId);
                showScenarioDeletedSuccessNotification();
                updateSearchParams({ scenarioId: undefined });
            },
            onError: (error: any) => {
                const errorMessage =
                    String(error?.response?.data?.detail || '').trim() ||
                    PricingSimulatorNotificationMessages.SCENARIO_ACTION_ERROR;
                sendNotification('error', errorMessage);
            },
        });
    }, [
        deleteScenarioMutation,
        projectId,
        refreshScenariosForProject,
        scenarios.length,
        selectedScenario,
        updateSearchParams,
    ]);

    const handleMoreActionsButtonClick = useCallback(() => {
        if (moreActionsConfirm) {
            closeMoreActionsConfirm();
            return;
        }
        setIsMoreActionsOpen((current) => !current);
    }, [closeMoreActionsConfirm, moreActionsConfirm]);

    const handleMoreActionsOpenChange = useCallback((open: boolean) => {
        setIsMoreActionsOpen(open);
    }, []);
    const moreActionsButtonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!isMoreActionsOpen) {
            return undefined;
        }

        const handleOutsideMouseDown = (event: MouseEvent) => {
            const target = event.target;
            if (!(target instanceof Node)) {
                return;
            }

            if (moreActionsButtonRef.current?.contains(target)) {
                return;
            }

            const dropdownMenu = document.querySelector('.more-actions-dropdown');
            if (dropdownMenu?.contains(target)) {
                return;
            }

            const submenuPopup = document.querySelector(
                `.ant-dropdown-menu-submenu-popup.${MORE_ACTIONS_SUBMENU_POPUP_CLASS}`,
            );
            if (submenuPopup?.contains(target)) {
                return;
            }

            setIsMoreActionsOpen(false);
        };

        document.addEventListener('mousedown', handleOutsideMouseDown);
        return () => document.removeEventListener('mousedown', handleOutsideMouseDown);
    }, [isMoreActionsOpen]);
    const duplicateScenarioTooltipTitle =
        isScenarioLimitReached && typeof childLimit === 'number'
            ? `You can only create ${childLimit} scenarios`
            : '';

    const confirmResetInputs = useCallback(() => {
        if (isResetInputsDisabled) {
            return;
        }
        setResetInputsModalOpen(true);
    }, [isResetInputsDisabled]);

    const closeResetInputsModal = useCallback(() => {
        setResetInputsModalOpen(false);
    }, []);

    const handleResetInputsConfirm = useCallback(async () => {
        if (!selectedScenario || !Number.isFinite(projectId)) {
            return;
        }

        const payload = buildResetScenarioPayload(projectId, selectedScenario.scenario_id);

        await new Promise<void>((resolve, reject) => {
            resetSimulatorMutation(payload, {
                onSuccess: async () => {
                    clearUserInputs();
                    await queryClient.invalidateQueries({ queryKey: [SIMULATION_TABLE_QUERY_KEY] });
                    await lensControllerRef.current?.resetToPersisted();
                    showResetInputsSuccessNotification();
                    setResetInputsModalOpen(false);
                    resolve();
                },
                onError: (error: unknown) => {
                    const errorMessage = resolvePpaCreateProjectApiErrorMessage(
                        error,
                        PricingSimulatorNotificationMessages.SCENARIO_ACTION_ERROR,
                    );
                    sendNotification('error', errorMessage);
                    reject(error);
                },
            });
        });
    }, [
        clearUserInputs,
        projectId,
        queryClient,
        resetSimulatorMutation,
        selectedScenario,
    ]);

    const moreActionItems = useMemo(
        () => [
            {
                key: DUPLICATE_SCENARIO_MENU_KEY,
                label: duplicateScenarioTooltipTitle ? (
                    <Tooltip
                        title={duplicateScenarioTooltipTitle}
                        color={colorConstants.black}
                        placement="top"
                        overlayInnerStyle={{ borderRadius: 8, fontSize: 16, fontWeight: 500 }}
                    >
                        <span data-testid={PPA_SIM_MORE_ACTIONS_DUPLICATE}>Duplicate Scenario</span>
                    </Tooltip>
                ) : (
                    <span data-testid={PPA_SIM_MORE_ACTIONS_DUPLICATE}>Duplicate Scenario</span>
                ),
                icon: <DuplicateIcon />,
                disabled: isDuplicateDisabled || !isScenarioOwner,
            },
            {
                key: BULK_UPDATE_MENU_KEY,
                label: <span data-testid={PPA_SIM_MORE_ACTIONS_BULK_UPDATE}>Bulk Update</span>,
                icon: <BulkUpdateIcon />,
                popupClassName: MORE_ACTIONS_SUBMENU_POPUP_CLASS,
                children: [
                    {
                        key: DOWNLOAD_TEMPLATE_MENU_KEY,
                        label: 'Download Template',
                        icon: <DownloadIcon stroke={colorConstants.blackTransparent88} />,
                    },
                    {
                        key: UPLOAD_FILE_MENU_KEY,
                        label: 'Upload File',
                        icon: <UploadOutlined style={{ fontSize: 16, color: colorConstants.blackTransparent88 }} />,
                    },
                ],
            },
            {
                key: DELETE_SCENARIO_MENU_KEY,
                label: deleteScenarioTooltipTitle ? (
                    <Tooltip
                        title={deleteScenarioTooltipTitle}
                        color={colorConstants.black}
                        placement="top"
                        overlayInnerStyle={{ borderRadius: 8, fontSize: 16, fontWeight: 500 }}
                    >
                        <span data-testid={PPA_SIM_MORE_ACTIONS_DELETE}>Delete Scenario</span>
                    </Tooltip>
                ) : (
                    <span data-testid={PPA_SIM_MORE_ACTIONS_DELETE}>Delete Scenario</span>
                ),
                icon: (
                    <TrashIcon
                        fill={
                            isDeleteScenarioDisabled
                                ? colorConstants.breadcrumbSlash
                                : colorConstants.growthDown
                        }
                    />
                ),
                danger: true,
                disabled: isDeleteScenarioDisabled,
            },
        ],
        [
            deleteScenarioTooltipTitle,
            duplicateScenarioTooltipTitle,
            isDeleteScenarioDisabled,
            isDuplicateDisabled,
            isScenarioOwner,
        ],
    );

    const handleMoreMenuAction = useCallback(
        ({ key }: { key: string }) => {
            if (!isScenarioOwner) {
                return;
            }
            if (key === DUPLICATE_SCENARIO_MENU_KEY) {
                confirmDuplicateScenario();
                return;
            }
            if (key === DOWNLOAD_TEMPLATE_MENU_KEY) {
                setIsMoreActionsOpen(false);
                setIsDownloadTemplateModalOpen(true);
                return;
            }
            if (key === UPLOAD_FILE_MENU_KEY) {
                setIsMoreActionsOpen(false);
                setIsBulkUploadModalOpen(true);
                return;
            }
            if (key === DELETE_SCENARIO_MENU_KEY) {
                confirmDeleteScenario();
            }
        },
        [confirmDeleteScenario, confirmDuplicateScenario, isScenarioOwner],
    );

    return (
        <div className="flex min-h-full flex-col gap-4" data-testid={PPA_SIM_PAGE}>
            <Global styles={moreActionsGlobalStyles} />
            <div className="flex min-h-[28px] flex-wrap items-center justify-between gap-x-[10px] gap-y-2 px-4">
                <Breadcrumb
                    basePath={basePath}
                    projectName={projectName}
                    scenarioName={selectedScenarioName}
                />
                {!isLoading && scenarioTabs.length ? (
                    <ScopeMetadataSummary
                        key={selectedScenarioId ?? 'none'}
                        projectName={projectName}
                        fields={scenarioMetadataSummary.fields}
                    />
                ) : null}
            </div>
            <div className="flex min-h-[calc(100vh-150px)] flex-col rounded-xl bg-white p-4 pt-0" data-testid={PPA_SIM_CONTENT}>
                <PriceSimulatorCreateEditModal
                    open={isCreateScenarioModalOpen}
                    formKind="scenario"
                    projectId={Number.isFinite(projectId) ? projectId : undefined}
                    scenarioActionType={PpaScenarioActionType.Create}
                    existingScenarios={scenarios}
                    modalTestId={PPA_SIM_CREATE_SCENARIO_MODAL}
                    cancelBtnTestId={PPA_SIM_CREATE_SCENARIO_CANCEL_BTN}
                    confirmBtnTestId={PPA_SIM_CREATE_SCENARIO_CONFIRM_BTN}
                    getFieldControlTestId={(inputId) =>
                        inputId === 'scenario_name' ? PPA_SIM_CREATE_SCENARIO_NAME_INPUT : undefined
                    }
                    onCancel={closeCreateScenarioModal}
                    onSubmitFinish={async (id) => {
                        let refreshedScenarios: typeof scenarios | undefined;
                        if (Number.isFinite(projectId)) {
                            refreshedScenarios = await refreshScenariosForProject(projectId);
                        }

                        const createdScenarioId =
                            typeof id === 'number' || typeof id === 'string' ? String(id) : null;
                        const nextScenarioId =
                            createdScenarioId ??
                            (refreshedScenarios?.length
                                ? String(refreshedScenarios[refreshedScenarios.length - 1].scenario_id)
                                : null);

                        updateSearchParams(
                            {
                                createScenario: undefined,
                                scenarioId: nextScenarioId ?? selectedScenarioId ?? undefined,
                            },
                            true,
                        );
                    }}
                />
                {isLoading ? (
                    <div className="flex min-h-[280px] items-center justify-center" data-testid={PPA_SIM_LOADING}>
                        <Spin />
                    </div>
                ) : scenarioTabs.length ? (
                    <div className="flex flex-1 flex-col gap-2">
                        <StyledScenarioTabsRow>
                            <StyledScenarioTabs
                                data-testid={PPA_SIM_SCENARIO_TABS}
                                type="card"
                                size="small"
                                activeKey={selectedScenarioId ?? scenarioTabs[0]?.key}
                                items={scenarioTabs}
                                onChange={handleScenarioTabChange}
                            />
                            {!isScenarioLimitReached ? (
                                <StyledNewScenarioTabButton
                                    type="button"
                                    data-testid={PPA_SIM_SCENARIO_TAB_ADD_BTN}
                                    onClick={handleNewScenarioClick}
                                >
                                    + New Scenario
                                </StyledNewScenarioTabButton>
                            ) : null}
                        </StyledScenarioTabsRow>
                        <StyledScenarioPanel
                            className="flex flex-1 flex-col border-x border-t border-borderLinePurpuleAlt"
                            data-testid={PPA_SIM_SCENARIO_PANEL}
                        >
                            <SimulationToolbar
                                toolbarConfig={simulationToolbarConfig}
                                scenario={{
                                    id: selectedScenario?.scenario_id,
                                    name: selectedScenarioName,
                                    ppaScopeId: selectedScenario?.ppa_scope_id ?? null,
                                    appliedScope: selectedScenario?.scope,
                                    isOwner: isScenarioOwner,
                                    renameRef: scenarioRenameRef,
                                    validateName: (name) =>
                                        validatePpaScenarioNameForRename(
                                            name,
                                            scenarios,
                                            selectedScenario?.scenario_id ?? null,
                                        ),
                                    onNameSave: handleRenameScenario,
                                    onRenamingChange: setIsScenarioRenaming,
                                }}
                                view={{
                                    isModified: isWorkspaceModified,
                                    viewId: viewSelection.viewId,
                                    dataSource: viewSelection.dataSource,
                                    lensControllerRef,
                                    onViewListLoadingChange: handleViewListLoadingChange,
                                    onSelectedViewChange: handleSelectedViewChange,
                                    onLoadedViewChange: handleLoadedViewChange,
                                    onViewLoaded: handleViewLoaded,
                                    onUpdateLoadedView: handleUpdateLoadedView,
                                    onPersistScenarioSnapshot: isScenarioOwner ? handlePersistScenarioSnapshot : undefined,
                                    onRequestUnsavedLeave: isScenarioOwner ? openLeavePrompt : undefined,
                                    onExitViewRequested: isScenarioOwner ? undefined : handleExitViewRequested,
                                    onScenarioExitComplete: handleScenarioExitComplete,
                                }}
                                search={{
                                    value: searchInput,
                                    onChange: handleSearchChange,
                                    onSubmit: handleSearchSubmit,
                                }}
                                filter={{
                                    isOpen: isFilterPanelOpen,
                                    isModified: isFilterModified,
                                    isActive: isFilterActive,
                                    categories: simulationFilterConfig.categories,
                                    editedActionTypes: simulationFilterConfig.showOnly.edited.actionTypes,
                                    appliedFilters,
                                    defaultShowOnly: defaultFilterShowOnly,
                                    showOnlyLabels: {
                                        edited: simulationFilterConfig.showOnly.edited.label,
                                        impacted: simulationFilterConfig.showOnly.impacted.label,
                                    },
                                    footerActions: simulationFilterConfig.footerActions,
                                    isApplyLoading: isResolvingTableFilters,
                                    onOpenChange: setIsFilterPanelOpen,
                                    onApply: handleAppliedFiltersChange,
                                }}
                                moreActions={{
                                    confirm: moreActionsConfirm,
                                    isOpen: isMoreActionsOpen,
                                    isDeleteSubmitting: isDeleteScenarioSubmitting,
                                    isDuplicateSubmitting: isDuplicateScenarioSubmitting,
                                    items: moreActionItems,
                                    buttonRef: moreActionsButtonRef,
                                    onOpenChange: handleMoreActionsOpenChange,
                                    onButtonClick: handleMoreActionsButtonClick,
                                    onMenuAction: handleMoreMenuAction,
                                    onDelete: handleDeleteScenario,
                                    onDuplicate: handleDuplicateScenario,
                                    onCloseConfirm: closeMoreActionsConfirm,
                                }}
                                simulateReset={{
                                    isSimulateDisabled,
                                    isResetDisabled: isResetInputsDisabled,
                                    resetTooltip: resetInputsTooltip,
                                    onReset: confirmResetInputs,
                                }}
                            />
                            <div className="relative flex min-h-[360px] flex-1 flex-col">
                                {showScenarioWorkspaceLoader ? (
                                    <div
                                        className="absolute inset-0 z-10 flex items-center justify-center bg-white"
                                        data-testid={PPA_SIM_WORKSPACE_LOADING}
                                    >
                                        <Spin size="small" />
                                    </div>
                                ) : null}
                                <SimulatorAppliedFiltersBar
                                    filterCategories={simulationFilterConfig.categories}
                                    editedActionTypes={simulationFilterConfig.showOnly.edited.actionTypes}
                                    appliedFilters={appliedFilters}
                                    isApplyLoading={isResolvingTableFilters}
                                    onAppliedFiltersChange={handleAppliedFiltersChange}
                                />
                                <div
                                    id={SCENARIO_WORKSPACE_CONTENT_ID}
                                    className="flex flex-1 flex-col "
                                    style={{ height: resolvedScenarioWorkspaceContentHeight }}
                                    data-testid={PPA_SIM_WORKSPACE}
                                >
                                    {!hasValidPpaScope ? (
                                        <div
                                            className="flex min-h-[280px] flex-1 items-center justify-center rounded-md bg-backgroundGreyAlt px-6 text-center"
                                            data-testid={PPA_SIM_NO_SCOPE}
                                        >
                                            <Empty description={missingPpaScopeMessage} />
                                        </div>
                                    ) : (
                                        <ScenarioLensSection
                                            key={`${selectedScenario?.scenario_id ?? 'none'}`}
                                            scenarioName={selectedScenarioName}
                                            scenarioId={selectedScenario?.scenario_id}
                                            viewId={resolvedViewId}
                                            dataSource={resolvedDataSource}
                                            isViewDetached={isViewDetached}
                                            viewListReady={isViewListReady}
                                            isOwner={isScenarioOwner}
                                            onDirtyChange={setIsScenarioViewModified}
                                            onContentLoadingChange={handleLensContentLoadingChange}
                                            onUserInputChange={markUserInputsDirty}
                                            onActiveLensIdChange={setActiveLensId}
                                            projectId={Number.isFinite(projectId) ? projectId : null}
                                            appliedFilters={appliedFilters}
                                            filterCategories={simulationFilterConfig.categories}
                                            searchInput={
                                                searchKeyword && simulationToolbarConfig.searchInputId
                                                    ? {
                                                          id: simulationToolbarConfig.searchInputId,
                                                          keyword: searchKeyword,
                                                      }
                                                    : undefined
                                            }
                                            resolvedTableFilters={resolvedTableFilters}
                                            editedActionTypes={simulationFilterConfig.showOnly.edited.actionTypes}
                                            lensControllerRef={lensControllerRef}
                                        />
                                    )}
                                </div>
                            </div>
                            <SaveOnLeavePrompt
                                open={leavePromptOpen}
                                viewName={loadedViewName || '—'}
                                canUpdateMainView={isCustomViewLoaded}
                                isSubmitting={isLeaveActionSubmitting}
                                onCancel={closeLeavePrompt}
                                onUpdateMainView={() => runLeaveAction(handleLeaveUpdateMainView)}
                                onIgnore={() => runLeaveAction(handleLeaveIgnore)}
                                onDiscard={() => runLeaveAction(handleLeaveDiscard)}
                            />
                            <ResetInputsConfirmationModal
                                open={resetInputsModalOpen}
                                confirmLoading={isResetInputsSubmitting}
                                onCancel={closeResetInputsModal}
                                onConfirm={handleResetInputsConfirm}
                            />
                        </StyledScenarioPanel>
                    </div>
                ) : (
                    <div
                        className="flex min-h-[280px] items-center justify-center rounded-md bg-backgroundGreyAlt"
                        data-testid={PPA_SIM_EMPTY}
                    >
                        <Empty description="No scenarios found for this project" />
                    </div>
                )}
            </div>
            {selectedScenario && Number.isFinite(projectId) ? (
                <>
                    <DownloadTemplateModalBridge
                        open={isDownloadTemplateModalOpen}
                        onClose={() => setIsDownloadTemplateModalOpen(false)}
                        scenarioId={selectedScenario.scenario_id}
                        projectId={projectId}
                        projectName={projectName}
                        scenarioName={selectedScenario.scenario_name}
                        createdBy={selectedScenario.created_by}
                    />
                    <BulkUploadModalBridge
                        open={isBulkUploadModalOpen}
                        onClose={() => setIsBulkUploadModalOpen(false)}
                        scenarioId={selectedScenario.scenario_id}
                        projectId={projectId}
                        projectName={projectName}
                        scenarioName={selectedScenario.scenario_name}
                        createdBy={selectedScenario.created_by}
                        onUploadSuccess={() => {
                            void refreshScenariosForProject(projectId);
                        }}
                    />
                </>
            ) : null}
        </div>
    );
};

export default PriceSimulatorV3ProjectScenarios;
